/**
 * AddMovieModal Component
 * Modal for searching TMDB and adding movies to a playlist
 */

import { useCallback, useEffect, useState } from 'react';
import { addMovieToPlaylist, searchTMDB, type PlaylistMovie, type TMDBSearchResult } from '../api';
import ModalPortal from './ModalPortal';

interface AddMovieModalProps {
  playlistId: number;
  onClose: () => void;
  onMovieAdded: (movie: PlaylistMovie) => void;
}

export default function AddMovieModal({ playlistId, onClose, onMovieAdded }: AddMovieModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TMDBSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<number | null>(null);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const movies = await searchTMDB(query.trim());
        setResults(movies);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleAddMovie = useCallback(
    async (movie: TMDBSearchResult) => {
      setAddingId(movie.tmdb_id);
      setError(null);

      try {
        const addedMovie = await addMovieToPlaylist(playlistId, movie.tmdb_id);
        onMovieAdded(addedMovie);
        // Remove from search results
        setResults((prev) => prev.filter((m) => m.tmdb_id !== movie.tmdb_id));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add movie');
      } finally {
        setAddingId(null);
      }
    },
    [playlistId, onMovieAdded]
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content add-movie-modal">
          <div className="modal-header">
            <h2>Add Movie</h2>
            <button className="modal-close" onClick={onClose}>
              &times;
            </button>
          </div>

          <div className="form-group">
            <input
              type="text"
              placeholder="Search for a movie..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="search-results">
            {loading && <div className="search-loading">Searching...</div>}

            {!loading && query.trim().length >= 2 && results.length === 0 && (
              <div className="search-empty">No movies found</div>
            )}

            {results.map((movie) => (
              <div key={movie.tmdb_id} className="search-result-item">
                {movie.poster_url ? (
                  <img src={movie.poster_url} alt={movie.title} className="search-result-poster" />
                ) : (
                  <div className="search-result-poster no-image">No image</div>
                )}
                <div className="search-result-info">
                  <h4>{movie.original_title || movie.title}</h4>
                  {movie.year && <span className="search-result-year">{movie.year}</span>}
                  {movie.overview && <p className="search-result-overview">{movie.overview}</p>}
                </div>
                <button
                  className="add-movie-btn"
                  onClick={() => handleAddMovie(movie)}
                  disabled={addingId === movie.tmdb_id}
                >
                  {addingId === movie.tmdb_id ? 'Adding...' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
